import { useMemo, useState } from "react";
import { useFilters } from "@/components/FilterBar";
import { QueryPanel } from "@/components/States";
import { Card, Select, Tag } from "@/components/ui";
import { useBreakdown, useMetaKeys } from "@/lib/api/queries";
import { formatLatency, formatNumber, formatPercent, formatUsd } from "@/lib/format";
import type { BreakdownRow } from "@/types/api";

const BASE_DIMENSIONS = [
  { value: "model", label: "모델별" },
  { value: "service", label: "서비스별" },
  { value: "provider", label: "프로바이더별" },
  { value: "environment", label: "환경별" },
  { value: "operation", label: "오퍼레이션별" },
  { value: "error_type", label: "에러 유형별" },
];

type SortKey = "total_cost_usd" | "total_tokens" | "calls" | "error_rate" | "avg_latency_ms";

const COLUMNS: Array<{ key: SortKey; label: string; render: (row: BreakdownRow) => string }> = [
  { key: "calls", label: "호출수", render: (row) => formatNumber(row.calls) },
  { key: "total_tokens", label: "총 토큰", render: (row) => formatNumber(row.total_tokens) },
  { key: "total_cost_usd", label: "비용", render: (row) => formatUsd(row.total_cost_usd) },
  { key: "error_rate", label: "에러율", render: (row) => formatPercent(row.error_rate) },
  { key: "avg_latency_ms", label: "평균 지연", render: (row) => formatLatency(row.avg_latency_ms) },
];

function value(row: BreakdownRow, key: SortKey): number {
  return key === "total_cost_usd" ? Number.parseFloat(row.total_cost_usd) : row[key];
}

export function Breakdown() {
  const [filters] = useFilters();
  const [dimension, setDimension] = useState("model");
  const [sort, setSort] = useState<SortKey>("total_cost_usd");
  const query = useBreakdown(filters, dimension);
  const metaKeys = useMetaKeys(filters);

  // Custom fields are first-class dimensions, same as model or service.
  const dimensions = [
    ...BASE_DIMENSIONS,
    ...(metaKeys.data?.keys ?? []).map((meta) => ({
      value: `metadata.${meta.key}`,
      label: `${meta.key} (커스텀)`,
    })),
  ];

  const rows = useMemo(
    () => [...(query.data?.rows ?? [])].sort((a, b) => value(b, sort) - value(a, sort)),
    [query.data, sort]
  );
  const totalCost = rows.reduce((sum, row) => sum + Number.parseFloat(row.total_cost_usd), 0);

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <Select
          label="기준"
          size="medium"
          value={dimension}
          options={dimensions}
          onChange={(event) => setDimension(event.target.value)}
          style={{ width: 220 }}
        />
        <span className="text-caption-1 text-label-assistive tabular-nums">
          {rows.length}개 · 합계 {formatUsd(totalCost)}
        </span>
      </div>

      <QueryPanel query={query} isEmpty={() => rows.length === 0}>
        {() => (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-label-1">
              <thead>
                <tr className="border-b border-line-neutral text-left">
                  <th scope="col" className="px-3 py-2 font-semibold text-label-alternative">
                    {dimensions.find((d) => d.value === dimension)?.label ?? dimension}
                  </th>
                  {COLUMNS.map((column) => (
                    <th key={column.key} scope="col" className="px-3 py-2 text-right font-semibold">
                      <button
                        type="button"
                        onClick={() => setSort(column.key)}
                        className={`rounded-xs px-1 transition-colors ${
                          sort === column.key ? "text-primary" : "text-label-alternative hover:text-label-normal"
                        }`}
                        aria-sort={sort === column.key ? "descending" : "none"}
                      >
                        {column.label}
                        {sort === column.key ? " ▾" : ""}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const share = totalCost > 0 ? Number.parseFloat(row.total_cost_usd) / totalCost : 0;
                  return (
                    <tr key={row.key} className="border-b border-line-alternative last:border-0">
                      <th scope="row" className="px-3 py-2 text-left font-medium text-label-normal">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{row.key}</span>
                          {share >= 0.2 && (
                            <Tag size="small" color="primary">
                              {formatPercent(share, 0)}
                            </Tag>
                          )}
                        </div>
                      </th>
                      {COLUMNS.map((column) => (
                        <td
                          key={column.key}
                          className={`px-3 py-2 text-right tabular-nums ${
                            column.key === "error_rate" && row.error_rate > 0.05
                              ? "text-status-negative"
                              : "text-label-normal"
                          }`}
                        >
                          {column.render(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </QueryPanel>
    </Card>
  );
}
